import React, { useEffect, useMemo, useState } from 'react';
import { loadThemeStyles } from './loadTheme';
import { ThemeContext, ThemeContextValue } from './ThemeContext';
import { useShallow } from 'zustand/react/shallow';
import { useStore } from '@/store/useStore';
import { addTheme, changeTheme, resetTheme } from '@/store/actions.ts';

export const ThemeProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const { theme, themes } = useStore(
    useShallow((s) => ({ theme: s.theme, themes: s.themes })),
  );
  const [isInitialThemeReady, setIsInitialThemeReady] = useState(false);

  useEffect(() => {
    let isCancelled = false;

    (async () => {
      try {
        await loadThemeStyles(theme);
        document.documentElement.setAttribute('data-theme', theme);
      } catch (e) {
        console.error(e);
      } finally {
        if (!isCancelled) {
          setIsInitialThemeReady(true);
        }
      }
    })();

    return () => {
      isCancelled = true;
    };
  }, [theme]);

  // Third-party widgets (e.g. @fanduel/rewards-hub-client) sometimes set their own
  // data-theme on document.documentElement instead of scoping to their own wrapper,
  // and don't revert it on unmount. Every theme stylesheet here is scoped to
  // :root[data-theme='<ours>'], so that clobbers the whole app's CSS custom
  // properties. Reassert our own value whenever something else changes it.
  useEffect(() => {
    const html = document.documentElement;

    const observer = new MutationObserver(() => {
      if (html.getAttribute('data-theme') !== theme) {
        html.setAttribute('data-theme', theme);
      }
    });
    observer.observe(html, { attributes: true, attributeFilter: ['data-theme'] });

    return () => observer.disconnect();
  }, [theme]);

  const value = useMemo<ThemeContextValue>(() => {
    return {
      theme,
      themes,
      addTheme: (name: string) => addTheme(name),
      changeTheme: (name: string) => changeTheme(name),
      resetTheme: () => resetTheme(),
    };
  }, [theme, themes]);

  return (
    <ThemeContext.Provider value={value}>
      {isInitialThemeReady ? children : null}
    </ThemeContext.Provider>
  );
};
